import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import React from "react";
import { StaticDatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useParams } from "react-router-dom";
import http from "src/utils/http";
import Constant from "src/utils/Constant";
import { useTranslation } from "react-i18next";
import StepperGuide from "src/components/StepperGuide";
import Booking from "src/utils/Booking";
import TimezonePicker from "src/views/general/timezone-picker";
import TreffasTextField from "src/components/TreffasTextField";
import Timezone from "src/utils/Timezone";
import { ArrowDropDown } from "@mui/icons-material";
import TreffasStyle from "src/utils/TreffasStyle";
import Helper from "src/utils/Helper";
import momentTimezone from "moment-timezone";
import { useFacebookPixelPublic } from "src/views/hooks/useFacebookPixelPublic";
import { useGoogleAnalyticsPublic } from "src/views/hooks/useGoogleAnalyticsPublic";

let dateFormat = "YYYY/MM/DD";

const DateAndTime = ({
  selectedTimezone,
  setSessionSelectedTime,
  selectedOffer,
  setSessionList,
  setBlockTimeOnSelectedDate,
  selectedDate,
  setSelectedDate,
  setError,
  clearErrors,
  calendarMaxDate,
  userDateFormat,
  setAllUpcommingAppt,
  preparingSessionList,
  setPreparingSesisonList,
  stepperGuideLength,
  setActiveStep,
  coachDetails,
  setOverFlowTimeToRender,
  setUnderFlowTimeToRender,
  setSelectedTimezone,
  isUseTimeRecord,
  setIsUseTimeRecord,
  timeRecords,
  setTimeRecords,
  customStyle,
}) => {
  const { t } = useTranslation();
  const { userId, offerId, organizationId } = useParams();

  const [timezoneDialog, setTimezoneDialog] = React.useState({ open: false });
  const [rerenderPixel, setRerenderPixel] = React.useState(1);

  const appointmentInterval =
    selectedOffer?.custom_options?.interval ?? Constant.interval;

  const appointmentSlots = selectedOffer?.slots;

  let listofOverFlowDates = [];
  let listofUnderFlowDates = [];

  let listofOverflow = {};
  let listofUnderflow = {};
  let listOfAvailableDates = {};

  const shouldDisabledDate = (date) => {
    let isWholeDayBlockedByCoach =
      Booking._dateandtime.isWholeDayBlockedByCoach(
        selectedOffer,
        date,
        coachDetails?.timezone_utc,
        selectedTimezone
      );

    if (isWholeDayBlockedByCoach) {
      return true; // calendar date is block by coach wholeday 00:00 to 23:59
    }

    let package_availability = Booking._dateandtime.getPackageAvailability(
      date,
      selectedOffer
    );

    // call fn to initialize listofOverFlowDates and listofUnderFlowDates variables
    Booking._dateandtime.generateSessionTime(
      package_availability,
      selectedOffer?.duration,
      date,
      selectedTimezone,
      coachDetails,
      appointmentInterval,
      listofOverFlowDates,
      listofOverflow,
      listofUnderFlowDates,
      listofUnderflow,
      listOfAvailableDates
    );

    let today = moment(date).format(dateFormat);
    let day = moment(date, "dddd").format("dddd");
    let daysEnabled = Booking._dateandtime.getDayEnableInPackage(
      date,
      selectedOffer
    );
    let dayIsEnable = Booking._dateandtime.isDayEnable(daysEnabled, day);
    // if isUseTimeRecord is true use states to match records if false use actual variables
    let hasOverflow = isUseTimeRecord
      ? timeRecords.listofOverFlowDates.includes(today.toString())
      : listofOverFlowDates.includes(today.toString());

    let hasUnderFlow = isUseTimeRecord
      ? timeRecords.listofUnderFlowDates.includes(today.toString())
      : listofUnderFlowDates.includes(today.toString());

    let hasSessions = isUseTimeRecord
      ? timeRecords.listOfAvailableDates[today]
      : listOfAvailableDates[today];

    if (hasOverflow) return false;
    if (hasUnderFlow) return false;
    if (!hasSessions && !hasOverflow && !hasUnderFlow) return true;
    if (!dayIsEnable) return false;
    return true;
  };

  const handleSelectedDate = async (date) => {
    if (preparingSessionList) return;
    setPreparingSesisonList(true);
    clearErrors();
    setSessionSelectedTime({});
    setSelectedDate(date);
    setIsUseTimeRecord(true);
    setActiveStep(1);
    // store prev records and use to rerender when back button is press
    setTimeRecords({
      listofOverFlowDates: listofOverFlowDates,
      listofUnderFlowDates: listofUnderFlowDates,
      listofOverflow: listofOverflow,
      listofUnderflow: listofUnderflow,
      listOfAvailableDates: listOfAvailableDates,
    });

    let today = moment(date).format(dateFormat);

    const gettodayAppt = await http.get(
      `/api/general/individual/offer/details/get-todays-offer-appts`,
      {
        params: {
          offer_id: offerId,
          organization_id: organizationId,
          user_id: userId,
          selected_date: today,
        },
      }
    );

    const getallUpcomingAppt = await http.get(
      `/api/general/individual/offer/details/get-all-upcomming-appts`,
      {
        params: {
          offer_id: offerId,
          organization_id: organizationId,
          user_id: userId,
          selected_date: today,
        },
      }
    );

    setAllUpcommingAppt(getallUpcomingAppt.data);

    if (gettodayAppt.data.length >= parseInt(appointmentSlots)) {
      setSessionList([]);
      setPreparingSesisonList(false);
      return;
    }

    setSessionList(
      listOfAvailableDates[today] ?? [] // if time has underflow get the yesterday data as start of availabeltime
    );
    setOverFlowTimeToRender(listofOverflow[today] ?? []);
    setUnderFlowTimeToRender(listofUnderflow[today] ?? []);
    setPreparingSesisonList(false);

    Booking._dateandtime.handleBlockedDate(
      date,
      selectedOffer,
      setBlockTimeOnSelectedDate
    );
  };

  const clientToday = momentTimezone(new Date(), Helper.zeroTimezone).tz(
    selectedTimezone
  );

  const user_settings = coachDetails?.settings
    ? JSON.parse(coachDetails?.settings)
    : null;
  const __analytics = user_settings?.analytics ?? null;
  let enable = __analytics?.has_meta_pixel ?? false;
  let pixelId = __analytics?.meta_pixel ?? null;
  let googleAnalyticsEnable = __analytics?.has_google_analytics ?? false;
  let gaId = __analytics?.google_analytics ?? null;

  const trackEventInFacebook = useFacebookPixelPublic({
    track: "ViewContent",
    content_name: `${selectedOffer?.name ?? ""} Create booking url`,
    content_description: `${
      selectedOffer?.description ?? "Package create booking url."
    }`,
    enable: enable,
    pixelId: pixelId,
  });

  const trackEventInGoogleAnalytics = useGoogleAnalyticsPublic({
    enable: googleAnalyticsEnable,
    gaId: gaId,
  });

  React.useEffect(() => {
    trackEventInFacebook();
    trackEventInGoogleAnalytics("view_content", {
      content_name: `${selectedOffer?.name ?? ""} Create booking url`,
      content_description: `${
        selectedOffer?.description ?? "Package create booking url."
      }`,
    });
  }, [rerenderPixel]);

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "32px",
            pt: "42px",
            flexDirection: "column",
          }}
        >
          <Box>
            <StepperGuide
              length={stepperGuideLength}
              activeLength={[1]}
              customStyle={customStyle}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily:
                  customStyle?.headingFont ?? "var(--font-grenettepromedium)",
                fontSize: "18px",
                lineHeight: "31px",
                alignItems: "center",
                color: customStyle?.textColor ?? "#060A2D",
              }}
            >
              {t("individual.booking.schedule.choose_schedule")}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "18px",
                color: customStyle?.secondaryTextColor ?? "#878787",
                fontFamily: `var(--font-${customStyle?.bodyFont})`,
                mt: "4px",
              }}
            >
              {t("individual.booking.schedule.choose_schedule.description", {
                package: selectedOffer?.name,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box my={"20px"}>
        <Box mb={"10px"}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "10px",
              lineHeight: "11px",
              letterSpacing: "0.07em",
              color: customStyle?.secondaryTextColor ?? "#878787",
              fontFamily: `var(--font-${customStyle?.bodyFont})`,
              textTransform: "uppercase",
            }}
          >
            {t("booking.label.select_timezone")}
          </Typography>
        </Box>

        <Box sx={{ px: "5px" }}>
          <Box>
            <TreffasTextField
              value={Timezone.getLabel(selectedTimezone) ?? ""}
              sx={TreffasStyle.formInputLarge("autocomplete")}
              placeholder={t("booking.placeholder.select_timezone")}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size={"small"}
                      onClick={() => {
                        setTimezoneDialog({ open: true });
                        setSelectedDate(new Date());
                      }}
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <TimezonePicker
                dialog={timezoneDialog}
                close={() => setTimezoneDialog({ open: false })}
                selectedTimezone={selectedTimezone}
                onChange={(data) => {
                  setSelectedTimezone(data);
                  setOverFlowTimeToRender([]);
                  setUnderFlowTimeToRender([]);
                  setSessionList([]);
                  setIsUseTimeRecord(false);
                  listofUnderflow = {};
                  listofOverflow = {};
                  listofOverFlowDates = [];
                  listofUnderFlowDates = [];
                  listOfAvailableDates = {};
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: "31px",
        }}
      >
        <Box className={"new-static-calendar"}>
          <Box>
            <StaticDatePicker
              // disablePast
              minDate={
                new Date(moment(clientToday, "YYYY/MM/DD").format("YYYY/MM/DD"))
              }
              displayStaticWrapperAs="desktop"
              value={selectedDate ? moment(selectedDate, "YYYY/MM/DD") : null}
              shouldDisableDate={(date) => shouldDisabledDate(date)}
              onChange={(value) => handleSelectedDate(value)}
              renderInput={(params) => <TextField {...params} />}
              views={["day"]}
              inputFormat={userDateFormat}
              maxDate={
                new Date(
                  moment(calendarMaxDate, "YYYY/MM/DD").format("YYYY/MM/DD")
                )
              }
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DateAndTime;
